import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

function getPasswordResetRedirectUrl(): string {
  const configuredUrl = (
    import.meta.env.VITE_APP_URL ||
    import.meta.env.VITE_SITE_URL ||
    import.meta.env.VITE_PUBLIC_URL ||
    ''
  ).trim();

  if (configuredUrl) {
    return configuredUrl.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return '';
}

export interface PasswordResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Request a password reset email (sent via Resend). Works without a session,
 * so it can be called from the login page.
 */
export async function requestPasswordReset(email: string): Promise<PasswordResponse> {
  // NOTE: the deployed function name is misspelled ("passwrod") — it was created
  // manually in the dashboard under that name. If it's ever redeployed as
  // "reset-password", update this URL to match.
  const redirectTo = getPasswordResetRedirectUrl();

  const response = await fetch(`${SUPABASE_URL}/functions/v1/reset-passwrod`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      redirectTo,
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    return {
      success: false,
      error: data?.error || `Request failed: ${response.status}`,
    };
  }

  return data as PasswordResponse;
}

/**
 * Admin-only: directly set a new password for any user.
 */
export async function adminSetPassword(userId: string, newPassword: string): Promise<PasswordResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return { success: false, error: 'Not authenticated' };
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-set-password`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, newPassword }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    return {
      success: false,
      error: data?.error || `Request failed: ${response.status}`,
    };
  }

  return data as PasswordResponse;
}

/**
 * Change the signed-in user's own password, verifying the current one first.
 */
export async function changeOwnPassword(
  email: string,
  currentPassword: string,
  newPassword: string
): Promise<PasswordResponse> {
  // Verify the current password by re-authenticating.
  const { error: verifyError } = await supabase.auth.signInWithPassword({
    email,
    password: currentPassword,
  });

  if (verifyError) {
    return { success: false, error: 'Current password is incorrect' };
  }

  const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });

  if (updateError) {
    return { success: false, error: updateError.message };
  }

  return { success: true };
}
